import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Ban,
  CreditCard,
  Download,
  Loader2,
  Move,
  Pencil,
  Plus,
  RefreshCw,
  Undo2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { PackageTag } from '@/components/package-tag';
import { env } from '@/config/env';
import { formatCurrency, formatDateTime, formatPhone } from '@/lib/format';
import { cn } from '@/lib/utils';
import { BOOKING_STATUS, bookingStatusMeta } from './event-enums';
import { HostPicker, type PickedUser } from './host-picker';
import { MoveBookingDialog } from './move-booking-dialog';
import { PackagePicker } from './package-picker';
import { RecordPaymentDialog } from './record-payment-dialog';
import {
  useCancelBooking,
  useCancelPurchase,
  useCreateBooking,
  useCreatePurchase,
  useRefundPurchase,
  useRequiresPackage,
  useTimeSlotBookings,
  useUpdateBooking,
} from './events.api';
import type { BookingEvent, EventBooking, EventTimeSlot } from './types';

/**
 * The people booked into one time slot, and everything that can be done to
 * those bookings: add, edit, cancel, charge, refund, move, record a payment
 * and export the list.
 */

/** Outstanding = what was charged minus what has been paid or refunded. */
function outstandingOn(booking: EventBooking): number {
  return (booking.purchases ?? []).reduce((total, purchase) => {
    const amount = purchase.amount ?? 0;
    const paid = purchase.amountPaid ?? 0;
    const refunded = purchase.refundAmount ?? 0;
    return total + Math.max(0, amount - paid - refunded);
  }, 0);
}

function toCsv(
  bookings: Record<string, unknown>[],
  headers: Record<string, string>
) {
  const keys = Object.keys(headers);
  const escape = (value: unknown) =>
    `"${String(value ?? '').replace(/"/g, '""')}"`;
  const lines = [
    keys.map((key) => escape(headers[key])).join(','),
    ...bookings.map((booking) =>
      keys.map((key) => escape(booking[key])).join(',')
    ),
  ];
  return lines.join('\n');
}

export function EventBookingsSheet({
  event,
  timeSlot,
  open,
  onOpenChange,
}: {
  event: BookingEvent | null;
  timeSlot: EventTimeSlot | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [addUser, setAddUser] = useState<PickedUser | null>(null);
  const [createCharge, setCreateCharge] = useState(true);
  const [addPackageId, setAddPackageId] = useState<string | null>(null);
  const [editing, setEditing] = useState<EventBooking | null>(null);
  const [movingBooking, setMovingBooking] = useState<EventBooking | null>(null);
  const [payingBooking, setPayingBooking] = useState<EventBooking | null>(null);
  const [refunding, setRefunding] = useState<{
    purchaseId: string;
    max: number;
  } | null>(null);
  const [refundAmount, setRefundAmount] = useState(0);

  const bookings = useTimeSlotBookings(open ? (timeSlot?.id ?? null) : null);
  // One active package on the event is enough to make the choice mandatory.
  const packageGate = useRequiresPackage(open ? event?.id : null);
  const createBooking = useCreateBooking();
  const cancelBooking = useCancelBooking();
  const createPurchase = useCreatePurchase();
  const cancelPurchase = useCancelPurchase();
  const refundPurchase = useRefundPurchase();

  if (!timeSlot) return null;

  const rows = bookings.data ?? [];
  const fail = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('events.bookingActionFailed')
    );

  function exportList() {
    // `package` is nested, and the CSV is flat — so it is flattened to the
    // one name the export needs before the rows go in.
    const flattened = rows.map((booking) => ({
      ...booking,
      packageName: booking.package
        ? (booking.package.nameEn ?? booking.package.nameEs ?? '')
        : '',
    }));
    const csv = toCsv(flattened, {
      firstName: t('events.firstName'),
      lastName: t('events.lastName'),
      phoneNumber: t('events.phone'),
      email: t('events.email'),
      status: t('events.status'),
      packageName: t('events.package'),
      bookedPrice: t('events.price'),
      createdAt: t('events.createdOn'),
      comments: t('events.comments'),
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings-${timeSlot!.id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>
              {t('events.bookings')}
              <span className="text-muted-foreground ml-1.5 font-normal tabular-nums">
                ({rows.length}/{timeSlot.maxCapacity ?? 0})
              </span>
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-5 p-4">
            {/* ------------------------------------------- add booking */}
            <section className="space-y-3 rounded-xl border p-3">
              <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {t('events.addBooking')}
              </p>

              <HostPicker
                label={t('events.customer')}
                selected={addUser}
                onSelect={setAddUser}
              />

              <PackagePicker
                eventId={open ? event?.id : null}
                value={addPackageId}
                onChange={setAddPackageId}
                label={t('events.package')}
                disabled={createBooking.isPending}
              />

              <div className="flex items-center justify-between gap-4">
                {/* The API field is `BypassPayment`; the question staff are
                    actually answering is whether to charge. */}
                <Label className="text-xs font-semibold">
                  {t('events.shouldCreateCharge')}
                </Label>
                <Switch
                  checked={createCharge}
                  onCheckedChange={setCreateCharge}
                />
              </div>

              <Button
                className="w-full"
                disabled={
                  !addUser ||
                  packageGate.loading ||
                  (packageGate.required && !addPackageId) ||
                  createBooking.isPending
                }
                onClick={() =>
                  createBooking.mutate(
                    {
                      userId: addUser!.id,
                      eventTimeSlotId: timeSlot!.id,
                      bypassPayment: !createCharge,
                      // Omitted entirely on an unpackaged event, which is what
                      // leaves those bookings behaving as they always have.
                      ...(addPackageId
                        ? { eventPackageId: addPackageId }
                        : {}),
                    },
                    {
                      onSuccess: () => {
                        toast.success(t('events.bookingCreated'));
                        setAddUser(null);
                        setAddPackageId(null);
                        void bookings.refetch();
                      },
                      onError: fail,
                    }
                  )
                }
              >
                {createBooking.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Plus />
                )}
                {t('events.addBooking')}
              </Button>
            </section>

            {/* ---------------------------------------------- toolbar */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void bookings.refetch()}
                disabled={bookings.isFetching}
              >
                <RefreshCw
                  className={cn(bookings.isFetching && 'animate-spin')}
                />
                {t('common.reload')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportList}
                disabled={rows.length === 0}
              >
                <Download />
                {t('events.exportList')}
              </Button>
            </div>

            {/* --------------------------------------------- bookings */}
            {bookings.isPending && (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="space-y-2 rounded-xl border p-3">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-52" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                ))}
              </div>
            )}

            {bookings.isError && (
              <p className="text-muted-foreground py-10 text-center text-sm">
                {t('events.bookingsFailed')}
              </p>
            )}

            {bookings.data && rows.length === 0 && (
              <div className="relative rounded-lg">
                <BusyOverlay show={bookings.isFetching} />
                <p
                  className={cn(
                    'text-muted-foreground rounded-lg border border-dashed py-10 text-center text-sm',
                    bookings.isFetching && 'opacity-60 transition-opacity'
                  )}
                >
                  {t('events.noBookings')}
                </p>
              </div>
            )}

            {/* A refresh or an action keeps the list readable under the
                spinner rather than blanking it. */}
            {rows.length > 0 && (
              <div className="relative space-y-5">
                <BusyOverlay show={bookings.isFetching} />
                {rows.map((booking) => {
                  const status = bookingStatusMeta(booking.status);
                  const outstanding = outstandingOn(booking);

                  return (
                    <div
                      key={booking.id}
                      className={cn(
                        'bg-card space-y-3 rounded-xl border p-3',
                        bookings.isFetching && 'opacity-60 transition-opacity'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium">
                            {[booking.firstName, booking.lastName]
                              .filter(Boolean)
                              .join(' ') || '—'}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {booking.email}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {formatPhone(booking.phoneNumber)}
                          </p>
                          {booking.package && (
                            <p className="mt-1.5 flex items-center gap-1.5">
                              <PackageTag
                                nameEn={booking.package.nameEn}
                                nameEs={booking.package.nameEs}
                              />
                              <span className="text-muted-foreground text-xs tabular-nums">
                                {formatCurrency(booking.bookedPrice)}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <Badge variant={status.tone}>{t(status.labelKey)}</Badge>
                          {outstanding > 0 && (
                            <span className="text-destructive text-xs tabular-nums">
                              {formatCurrency(outstanding)}{' '}
                              {t('events.outstanding').toLowerCase()}
                            </span>
                          )}
                        </div>
                      </div>

                      <p className="text-muted-foreground text-xs">
                        {t('events.createdOn')}: {formatDateTime(booking.createdAt)}
                      </p>

                      {booking.comments && (
                        <p className="bg-muted rounded p-2 text-xs">
                          {booking.comments}
                        </p>
                      )}

                      {/* ------------------------------------ purchases */}
                      {(booking.purchases ?? []).length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                            {t('events.payments')}
                          </p>
                          {(booking.purchases ?? []).map((purchase) => (
                            <div
                              key={purchase.id}
                              className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-xs"
                            >
                              <div>
                                <p className="tabular-nums">
                                  {formatCurrency(purchase.amount)}
                                  {purchase.amountPaid ? (
                                    <span className="text-muted-foreground">
                                      {' '}
                                      · {t('events.paid')}{' '}
                                      {formatCurrency(purchase.amountPaid)}
                                    </span>
                                  ) : null}
                                  {purchase.refundAmount ? (
                                    <span className="text-muted-foreground">
                                      {' '}
                                      · {t('events.refunded')}{' '}
                                      {formatCurrency(purchase.refundAmount)}
                                    </span>
                                  ) : null}
                                </p>
                                <p className="text-muted-foreground">
                                  {formatDateTime(purchase.createdAt)}
                                </p>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    setRefunding({
                                      purchaseId: purchase.id,
                                      max: purchase.amountPaid ?? 0,
                                    });
                                    setRefundAmount(purchase.amountPaid ?? 0);
                                  }}
                                >
                                  <Undo2 className="size-3" />
                                  {t('events.refund')}
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs"
                                    >
                                      {t('events.cancelPurchase')}
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        {t('events.cancelPurchase')}
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t('events.cancelPurchaseBody', {
                                          fee: formatCurrency(
                                            env.bookingCancellationFee
                                          ),
                                        })}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        {t('common.cancel')}
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          cancelPurchase.mutate(
                                            {
                                              purchaseId: purchase.id,
                                              cancellationFee:
                                                env.bookingCancellationFee,
                                            },
                                            {
                                              onSuccess: () => {
                                                toast.success(
                                                  t('events.purchaseCancelled')
                                                );
                                                void bookings.refetch();
                                              },
                                              onError: fail,
                                            }
                                          )
                                        }
                                      >
                                        {t('events.cancelPurchase')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* -------------------------------------- actions */}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(booking)}
                        >
                          <Pencil className="size-3.5" />
                          {t('events.editBooking')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMovingBooking(booking)}
                        >
                          <Move className="size-3.5" />
                          {t('events.moveBooking')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPayingBooking(booking)}
                        >
                          <CreditCard className="size-3.5" />
                          {t('events.recordPayment')}
                        </Button>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              {t('events.createPaymentRequest')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t('events.createPaymentRequest')}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('events.createPurchaseBody', {
                                  amount: formatCurrency(event?.price ?? 0),
                                })}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {t('common.cancel')}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  createPurchase.mutate(
                                    {
                                      bookingId: booking.id,
                                      userId: booking.userId ?? '',
                                      amount: event?.price ?? 0,
                                    },
                                    {
                                      onSuccess: () => {
                                        toast.success(t('events.purchaseCreated'));
                                        void bookings.refetch();
                                      },
                                      onError: fail,
                                    }
                                  )
                                }
                              >
                                {t('events.create')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Ban className="size-3.5" />
                              {t('events.cancelBooking')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t('events.cancelBooking')}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('events.cancelBookingBody', {
                                  fee: formatCurrency(env.bookingCancellationFee),
                                })}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {t('common.cancel')}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() =>
                                  cancelBooking.mutate(
                                    {
                                      bookingId: booking.id,
                                      cancellationFee: env.bookingCancellationFee,
                                    },
                                    {
                                      onSuccess: () => {
                                        toast.success(t('events.bookingCancelled'));
                                        void bookings.refetch();
                                      },
                                      onError: fail,
                                    }
                                  )
                                }
                              >
                                {t('events.cancelBooking')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <EditBookingDialog
        booking={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        onSaved={() => void bookings.refetch()}
      />

      <MoveBookingDialog
        booking={movingBooking}
        open={movingBooking !== null}
        onOpenChange={(next) => {
          if (!next) setMovingBooking(null);
        }}
        onMoved={() => void bookings.refetch()}
      />

      <RecordPaymentDialog
        targetId={payingBooking?.id ?? null}
        purchases={payingBooking?.purchases ?? []}
        open={payingBooking !== null}
        onOpenChange={(next) => {
          if (!next) setPayingBooking(null);
        }}
        onSaved={() => void bookings.refetch()}
      />

      {/* ------------------------------------------------ refund modal */}
      <AlertDialog
        open={refunding !== null}
        onOpenChange={(next) => {
          if (!next) setRefunding(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('events.refund')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('events.refundBody', {
                max: formatCurrency(refunding?.max ?? 0),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.refundAmount')}
            </Label>
            <Input
              type="number"
              min={0}
              max={refunding?.max ?? 0}
              step="0.01"
              value={refundAmount}
              onChange={(e) => setRefundAmount(Number(e.target.value))}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={refundPurchase.isPending || refundAmount <= 0}
              onClick={() =>
                refundPurchase.mutate(
                  {
                    purchaseId: refunding!.purchaseId,
                    refundAmount: refundAmount,
                  },
                  {
                    onSuccess: () => {
                      toast.success(t('events.refunded'));
                      setRefunding(null);
                      void bookings.refetch();
                    },
                    onError: fail,
                  }
                )
              }
            >
              {t('events.refund')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ------------------------------------------------------ edit booking modal

function EditBookingDialog({
  booking,
  open,
  onOpenChange,
  onSaved,
}: {
  booking: EventBooking | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateBooking();
  const [hydrated, setHydrated] = useState<string | null>(null);
  const [status, setStatus] = useState(0);
  const [comments, setComments] = useState('');
  const [info1, setInfo1] = useState('');
  const [info2, setInfo2] = useState('');

  if (booking && hydrated !== booking.id) {
    setHydrated(booking.id);
    setStatus(booking.status ?? 0);
    setComments(booking.comments ?? '');
    setInfo1(booking.additionalInformation1Response ?? '');
    setInfo2(booking.additionalInformation2Response ?? '');
  }

  if (!booking) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('events.editBooking')}</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">
            {t('events.editBooking')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.status')}
            </Label>
            <Select
              value={String(status)}
              onValueChange={(value) => setStatus(Number(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BOOKING_STATUS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.comments')}
            </Label>
            <Textarea
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.additionalInformation')} 1
            </Label>
            <Input value={info1} onChange={(e) => setInfo1(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.additionalInformation')} 2
            </Label>
            <Input value={info2} onChange={(e) => setInfo2(e.target.value)} />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                {
                  bookingId: booking.id,
                  status: status,
                  comments: comments,
                  additionalInformation1Response: info1,
                  additionalInformation2Response: info2,
                },
                {
                  onSuccess: () => {
                    toast.success(t('events.bookingUpdated'));
                    onOpenChange(false);
                    onSaved();
                  },
                  onError: () => toast.error(t('events.bookingActionFailed')),
                }
              )
            }
          >
            {update.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('common.save')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
